import * as React from "react";
import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";

import { cn } from "../../lib/utils";
import { IconChevronRight, IconDots } from "@tabler/icons-react";

function Breadcrumb({ className, ...props }: React.ComponentProps<"nav">) {
  return (
    <nav
      aria-label="breadcrumb"
      data-slot="breadcrumb"
      className={cn(className)}
      {...props}
    />
  );
}

function BreadcrumbList({ className, ...props }: React.ComponentProps<"ol">) {
  return (
    <ol
      data-slot="breadcrumb-list"
      className={cn(
        "[list-style-type:none] [list-style-position:inside] [display:flex] [flex-wrap:nowrap] [align-items:center] [gap:0.5rem] [padding-inline:0.25rem] [margin:0rem] [font-size:0.875rem] [line-height:1.25rem] [color:var(--muted-foreground)] [white-space:nowrap]",
        className,
      )}
      {...props}
    />
  );
}

function BreadcrumbItem({ className, ...props }: React.ComponentProps<"li">) {
  return (
    <li
      data-slot="breadcrumb-item"
      className={cn(
        "[display:inline-flex] [min-width:0rem] [align-items:center] [gap:0.375rem]",
        className,
      )}
      {...props}
    />
  );
}

function BreadcrumbLink({
  className,
  render,
  ...props
}: useRender.ComponentProps<"a">) {
  return useRender({
    defaultTagName: "a",
    props: mergeProps<"a">(
      {
        className: cn(
          "[display:inline-flex] [cursor:pointer] [color:var(--muted-foreground)] [white-space:nowrap] [text-overflow:ellipsis] [overflow:hidden] [text-decoration-line:none] outline-none hover:[color:var(--foreground)] focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
          className,
        ),
      },
      props,
    ),
    render,
    state: {
      slot: "breadcrumb-link",
    },
  });
}

function BreadcrumbPage({ className, ...props }: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="breadcrumb-page"
      role="link"
      aria-disabled="true"
      aria-current="page"
      className={cn(
        "[font-weight:500] [color:var(--foreground)] [white-space:nowrap] [text-overflow:ellipsis] [overflow:hidden]",
        className,
      )}
      {...props}
    />
  );
}

function BreadcrumbSeparator({
  children,
  className,
  ...props
}: React.ComponentProps<"li">) {
  return (
    <li
      data-slot="breadcrumb-separator"
      role="presentation"
      aria-hidden="true"
      className={cn(
        "[display:inline-flex] [align-items:center] [color:var(--muted-foreground)] [flex-shrink:0] [&>svg]:[width:0.875rem] [&>svg]:[height:0.875rem]",
        className,
      )}
      {...props}
    >
      {children ?? <IconChevronRight />}
    </li>
  );
}

function BreadcrumbEllipsis({
  className,
  ...props
}: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="breadcrumb-ellipsis"
      role="presentation"
      aria-hidden="true"
      className={cn(
        "[display:flex] [width:1.25rem] [height:1.25rem] [align-items:center] [justify-content:center] outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background [&>svg]:[width:1rem] [&>svg]:[height:1rem]",
        className,
      )}
      {...props}
    >
      <IconDots />
      <span className={"sr-only"}>More</span>
    </span>
  );
}

export {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
  BreadcrumbEllipsis,
};
